import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck, Lock, CheckCircle2 } from "lucide-react";

export default function SetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [notValid, setNotValid] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setNotValid("Missing invite token."); return; }
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setNotValid(e?.response?.data?.detail || "This invite link is invalid or has expired."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password: pw });
      localStorage.setItem("pinpoint_token", data.token);
      toast.success("Password set — welcome to PinPoint!");
      navigate("/");
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set password"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 text-blue-300 text-xs font-semibold uppercase tracking-widest mb-4 justify-center"><ShieldCheck size={14} /> Activate your account</div>
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-7" data-testid="set-password-card">
          {notValid ? (
            <div className="text-center py-6">
              <Lock className="mx-auto mb-3 text-slate-400" size={30} />
              <h1 className="text-lg font-bold text-slate-900">Link not valid</h1>
              <p className="text-sm text-slate-500 mt-1">{notValid}</p>
              <button onClick={() => navigate("/login")} className="mt-5 text-sm font-medium text-blue-700 hover:text-blue-800">Go to login</button>
            </div>
          ) : !invite ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-blue-600" size={26} /></div>
          ) : (
            <form onSubmit={submit}>
              <div className="flex items-center gap-2 mb-1"><KeyRound size={18} className="text-blue-600" /><h1 className="font-display text-xl font-bold text-slate-900">Set your password</h1></div>
              <p className="text-sm text-slate-500 mb-5">Welcome, <b>{invite.name}</b>. Create a password for <b>{invite.email}</b> to access your {invite.role === "tenant" ? "tenant portal" : "account"}.</p>
              <label className="block text-sm font-medium text-slate-700 mb-1">New password</label>
              <input data-testid="new-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <label className="block text-sm font-medium text-slate-700 mb-1">Confirm password</label>
              <input data-testid="confirm-password" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Re-enter your password"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <button type="submit" data-testid="set-password-submit" disabled={busy}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Set password & sign in
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-slate-500 mt-4">Powered by PinPoint</p>
      </div>
    </div>
  );
}
